/* eslint-disable @typescript-eslint/no-explicit-any */

import IconMinus from '@assets/icons/minus.svg?react';
import IconPlus from '@assets/icons/plus.svg?react';
import {
 Box,
 Button,
 Card,
 CardContent,
 Collapse,
 IconButton,
 Typography,
 useTheme,
} from '@mui/material';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import LazySvg from '../LazySvg';
import EditFields from './EditFields';
import useEditArray from './useEditArray';

const EditArray = ({ arrayKey }: { arrayKey: string }) => {
 const { t } = useTranslation();
 const th = useTheme();

 const {
  onSubmit,
  formMethods,
  loading,
  submitDisabled,
  fieldsWithData,
  handleEdit,
  handleCancel,
  handleDelete,
  showCreateForm,
  currentItem,
  editingId,
  handleCreate,
 } = useEditArray({ arrayKey });
 const renderItemEditForm = (isEdit = false) => (
  <Card sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
   <CardContent
    sx={{
     backgroundColor: th.palette.kemek.lightBlue,
     px: 1,
     pt: 2,
     pb: '16px !important',
    }}
   >
    <Box component="form" onSubmit={formMethods.handleSubmit(onSubmit)}>
     <EditFields fieldsWithData={fieldsWithData} />
     <Box
      sx={{
       display: 'flex',
       alignItems: 'center',
       justifyContent: 'space-between',
       gap: 1,
       px: 1,
       mt: '12px',
      }}
     >
      <Button
       sx={{ p: 0 }}
       variant="text"
       color="inherit"
       onClick={handleCancel}
       startIcon={<IconMinus style={{ color: th.palette.kemek.blue }} />}
      >
       {t('global.cancel')}
      </Button>
      <Button
       sx={{ p: 0 }}
       variant="text"
       color="primary"
       type="submit"
       disabled={submitDisabled}
       endIcon={
        <IconPlus
         style={{
          color: submitDisabled ? th.palette.kemek.gray : th.palette.kemek.blue,
         }}
        />
       }
      >
       {isEdit ? t('global.save') : t('global.create')}
      </Button>
     </Box>
    </Box>
   </CardContent>
  </Card>
 );

 const renderItem = (arrayItem: any) => {
  const isEditing = editingId === arrayItem.Id;
  if (isEditing) {
   return <Box key={arrayItem.Id}>{renderItemEditForm(true)}</Box>;
  }

  return (
   <Card key={arrayItem.Id} sx={{ border: '1px solid #f0f0f0' }}>
    <CardContent sx={{ p: '12px', '&:last-child': { pb: '12px' } }}>
     <Box
      sx={{
       display: 'flex',
       justifyContent: 'space-between',
       alignItems: 'flex-start',
      }}
     >
      <Box sx={{ flex: 1 }}>
       {fieldsWithData.map((field) => (
        <Box
         key={field.Name}
         sx={{
          mb: 0.5,
          wordBreak: 'break-all',
          // display: 'flex',
          // alignItems: 'center',
          // gap: 1,
         }}
        >
         <Typography sx={{ fontWeight: 600, display: 'inline' }}>
          {field.Translation}:{' '}
         </Typography>
         <Typography sx={{ display: 'inline' }}>
          {['number', 'string'].includes(typeof arrayItem[field.Name])
           ? arrayItem[field.Name]
           : arrayItem[field.Name]?.Name}
         </Typography>
        </Box>
       ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
       <IconButton
        onClick={() => handleEdit(arrayItem.Id)}
        sx={{ color: 'primary.main' }}
        disabled={!!loading.updateItem}
       >
        <LazySvg name="edit" styles={{ width: 16 }} />
       </IconButton>

       <IconButton
        onClick={() => handleDelete(arrayItem.Id)}
        sx={{ color: 'error.main' }}
        disabled={!!loading.deleteItem}
       >
        <LazySvg name="delete" styles={{ width: 16 }} />
       </IconButton>
      </Box>
     </Box>
    </CardContent>
   </Card>
  );
 };

 return (
  <FormProvider {...formMethods}>
   <Box
    sx={{
     display: 'flex',
     justifyContent: 'space-between',
     alignItems: 'center',
     mb: 2,
    }}
   >
    <Typography
     sx={{
      fontWeight: 500,
      fontSize: '14px',
      color: th.palette.kemek.darkGray,
     }}
    >
     {t(`entities.${arrayKey.charAt(0).toLowerCase()}${arrayKey.slice(1)}`)}
    </Typography>

    <IconButton
     color="primary"
     onClick={handleCreate}
     disabled={showCreateForm || !!loading.getItem}
    >
     <IconPlus />
    </IconButton>
   </Box>

   <Collapse in={showCreateForm}>{renderItemEditForm(false)}</Collapse>

   {currentItem?.length === 0 ? (
    <Box
     sx={{
      textAlign: 'center',
      p: '12px',
      backgroundColor: th.palette.kemek.lightBlue,
     }}
    >
     <Typography variant="body2" color="text.secondary">
      {t('global.noItemsFound')}
     </Typography>
    </Box>
   ) : (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
     {currentItem?.map(renderItem)}
    </Box>
   )}
  </FormProvider>
 );
};

export default EditArray;
